import { HTMLMotionProps, motion } from 'framer-motion';
import React, { forwardRef } from 'react';
import { useNavigationType } from 'react-router-dom';

import { EASE_OUT_QUINT } from '../constants/animations';
import { getInvertedX } from '../utils/get-inverted-x';

export type AnimatedFadeComponentProps = HTMLMotionProps<'div'> & {
  x?: string | number;
  scale?: number;
  duration?: number;
  direction?: 'forwards' | 'backwards';
  children?: React.ReactNode;
};

export const AnimatedFadeComponent = forwardRef<
  HTMLDivElement,
  AnimatedFadeComponentProps
>(
  (
    {
      children,
      x = 2,
      scale = 0.92,
      direction = 'forwards',
      duration,
      ...otherProps
    },
    ref,
  ) => {
    const navigationType = useNavigationType();

    const inX = navigationType === 'POP' ? getInvertedX(x) : x;
    const outX = navigationType === 'POP' ? x : getInvertedX(x);

    const variants = {
      initial: {
        opacity: 0,
        scale,
        x: direction === 'forwards' ? inX : outX,
      },
      in: {
        opacity: 1,
        scale: 1,
        x: 0,
        transition: EASE_OUT_QUINT,
      },
      out: {
        opacity: 0,
        scale,
        x: direction === 'forwards' ? outX : inX,
        transition: EASE_OUT_QUINT,
      },
    } as const;

    if (duration !== undefined) {
      variants.in.transition.duration = duration;
      variants.out.transition.duration = duration;
    }

    return (
      <motion.div
        ref={ref}
        initial="initial"
        animate="in"
        exit="out"
        variants={variants}
        {...otherProps}
      >
        {children}
      </motion.div>
    );
  },
);

AnimatedFadeComponent.displayName = 'AnimatedFadeComponent';
